import { AppointmentModel } from "@devexpress/dx-react-scheduler";

import {
  max as dateMax,
  min as dateMin,
  differenceInMinutes,
  isBefore
} from "date-fns";
import { max, sortBy } from "lodash";
import { v4 as uuidV4 } from "uuid";

import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_UNCONFIRMED,
  EVENT_TYPE_APPOINTMENT,
  EVENT_TYPE_APPOINTMENT_BLOCK,
  EVENT_TYPE_CLINIC_EVENT,
  EVENT_TYPE_PERSONAL_EVENT,
  EVENT_TYPE_PERSONAL_WORKSHIFT,
  EVENT_TYPE_SCHEDULE_CODE,
  EVENT_TYPE_WORK_SHIFT_CODE
} from "constants/appointments";
import { Appointment } from "services/types/appointments.type";
import {
  type AppointmentBlock,
  type AppointmentBlockEvent,
  type AppointmentEvent,
  type ClinicEvent,
  type EventResponse,
  type EventSearchOption,
  type Events,
  type EventsResponse,
  type PersonalEvent,
  type PersonalWorkShiftEvent,
  type ScheduleEvent,
  type WorkShiftEvent
} from "services/types/events.type";
import { Room } from "services/types/rooms.type";
import { MinimalStaffMember } from "services/types/staff.type";

import { buildStaffMemberWorkShift } from "./build-staff-member-work-shift.util";
import {
  convertDateInTimezoneToCurrent,
  formatStaffMemberFullName
} from "./common";
import { formatPatient } from "./patients.util";
import { formatRoomClinic } from "./rooms.util";

export function convertEventToAppointmentModel(
  event: AppointmentEvent,
  timeZone?: string
): Appointment {
  const toMomentString = (raw: string | null | undefined): string | null => {
    if (!raw) return null;
    const converted = convertDateInTimezoneToCurrent(raw, timeZone);
    return converted instanceof Date
      ? converted.toISOString()
      : String(converted);
  };

  const appointment: Appointment = {
    id: event.extra.appointment_id,
    title: event.title,
    type: "Appointment",
    patient: event.extra.patient && formatPatient(event.extra.patient),
    webPatient: event.extra.extra || null,
    startDate: convertDateInTimezoneToCurrent(
      event.start_moment,
      timeZone
    ).toISOString(),
    endDate: convertDateInTimezoneToCurrent(
      event.end_moment,
      timeZone
    ).toISOString(),
    staffMember: formatStaffMemberFullName(
      event.staff_member as MinimalStaffMember
    ),
    staffMemberId: Number((event.staff_member as MinimalStaffMember)?.id),
    appointmentType: event.extra?.appointment_type,
    room: event.extra.room && formatRoomClinic(event.extra.room as Room),
    clinic: event.clinic,
    clinicId: event.clinic.id,
    referral: event.extra?.physician_referral,
    referralSource: event.extra?.referral_source || null,
    subReferralSource: event.extra?.sub_referral_source || null,
    referringPhysician: event.extra.referring_physician || null,
    companionType: event.extra?.companion_type,
    companionName: event.extra?.companion_name,
    notes: event.extra?.notes,
    status: event.extra?.status,
    is_opportunity: event.extra?.is_opportunity,
    shouldNotify: event.extra.should_notify,
    shouldCreateScheduleForStaff: event.extra.should_create_schedule_for_staff,
    chartNoteId: event.extra.chart_note,
    assignedTags: event.extra.assigned_tags,
    notificationMethod: event.extra.notification_method,
    notifyBothChannels: event.extra.notify_both_channels,
    created_at: event.created_at,
    created_by: event.created_by,
    updated_at: event.updated_at,
    updated_by: event.updated_by,
    needs_reschedule: event.extra?.needs_reschedule,
    // Only when the backend sent the key (ENG-2104): V3 reads "absent" and "null" apart.
    ...("reminded_at" in (event.extra ?? {})
      ? { reminded_at: event.extra?.reminded_at ?? null }
      : {}),
    schedule: event.extra?.schedule,
    start_time: "",
    end_time: "",
    supervising_provider: event.extra?.supervising_provider
      ? formatStaffMemberFullName(event.extra?.supervising_provider)
      : null,
    is_direct_supervisor: event.extra?.is_direct_supervisor,
    googleCalendar: (event.extra?.extra as any)?.google_calendar ?? null,
    source: event.extra?.source || null,
    zoom_join_url: event.extra?.zoom_join_url || null,
    zoom_meeting_id: event.extra?.zoom_meeting_id || null,
    room_start_moment: toMomentString(event.extra?.room_start_moment),
    room_end_moment: toMomentString(event.extra?.room_end_moment)
  };
  return appointment;
}

export function convertEventToPersonalEventModel(
  event: ScheduleEvent,
  timeZone?: string
): PersonalEvent | ClinicEvent {
  const id = uuidV4();
  const modelId = event.extra.schedule_id;
  const rrule = event.extra?.rrule;

  const personalEvent: PersonalEvent = {
    type: "Personal",
    id,
    modelId,
    title: event.title,
    notes: event.extra?.notes,
    recurrencePeriod: event.extra.recurrence_period,
    recurrenceDay: event.extra.recurrence_day,
    recurrenceWeek: event.extra.recurrence_week,
    startDate: convertDateInTimezoneToCurrent(
      event.start_moment,
      timeZone
    ).toISOString(),
    endDate: convertDateInTimezoneToCurrent(
      event.end_moment,
      timeZone
    ).toISOString(),
    endRecurrenceDate: event.extra.end_date,
    clinic: event.clinic,
    clinicId: event.clinic.id,
    isClinicEvent: false,
    isGlobalPracticeEvent: event.extra.is_global_practice_event,
    staffMember: formatStaffMemberFullName(
      event.staff_member as MinimalStaffMember
    ),
    staffMemberId: Number((event.staff_member as MinimalStaffMember)?.id),
    rRule: "",
    created_at: event.created_at,
    created_by: event.created_by,
    updated_at: event.updated_at,
    updated_by: event.updated_by,
    start_moment: event.start_moment,
    end_moment: event.end_moment,
    appointment_type: event.extra.appointment_types[0],
    staff_members: event.extra.staff_members.map((staffMember) =>
      formatStaffMemberFullName(staffMember)
    ),
    rrule
  };
  if (event.extra.is_clinic_event) {
    return {
      ...personalEvent,
      type: "Clinic",
      isClinicEvent: event.extra.is_clinic_event
    } as ClinicEvent;
  }
  return personalEvent;
}

export function convertEventToWorkShiftEventModel(
  event: WorkShiftEvent,
  timeZone?: string
): PersonalWorkShiftEvent {
  const id = uuidV4();
  const modelId = event.extra.schedule_id;
  const rrule = event.extra?.rrule;
  const personalWorkShiftEvent: PersonalWorkShiftEvent = {
    type: "WorkShift",
    id,
    modelId,
    title: event.title,
    notes: event.extra?.notes,
    recurrencePeriod: event.extra.recurrence_period,
    recurrenceDay: event.extra.recurrence_day,
    recurrenceWeek: event.extra.recurrence_week,
    startDate: convertDateInTimezoneToCurrent(
      event.start_moment,
      timeZone
    ).toISOString(),
    endDate: convertDateInTimezoneToCurrent(
      event.end_moment,
      timeZone
    ).toISOString(),
    endRecurrenceDate: event.extra.end_date,
    clinic: event.clinic,
    clinicId: event.clinic.id,
    isClinicEvent: false,
    isGlobalPracticeEvent: event.extra.is_global_practice_event,
    staffMember: formatStaffMemberFullName(
      event.staff_member as MinimalStaffMember
    ),
    staffMemberId: Number((event.staff_member as MinimalStaffMember).id),
    rRule: "",
    rrule,
    created_at: event.created_at,
    created_by: event.created_by,
    updated_at: event.updated_at,
    updated_by: event.updated_by,
    start_moment: event.start_moment,
    end_moment: event.end_moment
  };
  return personalWorkShiftEvent;
}

export function convertEventToAppointmentBlockModel(
  event: AppointmentBlockEvent,
  timeZone?: string
): AppointmentBlock {
  const id = uuidV4();
  const modelId = event.extra.schedule_id;
  const rrule = event.extra?.rrule;
  const appointmentBlock: AppointmentBlock = {
    type: "Block",
    id,
    modelId,
    title: event.title,
    notes: event.extra?.notes,
    recurrencePeriod: event.extra.recurrence_period,
    recurrenceDay: event.extra.recurrence_day,
    recurrenceWeek: event.extra.recurrence_week,
    startDate: (
      convertDateInTimezoneToCurrent(event.start_moment, timeZone) as Date
    ).toISOString(),
    endDate: (
      convertDateInTimezoneToCurrent(event.end_moment, timeZone) as Date
    ).toISOString(),
    endRecurrenceDate: event.extra.end_date,
    clinic: event.clinic,
    clinicId: event.clinic.id,
    isClinicEvent: event.extra.is_clinic_event,
    isGlobalPracticeEvent: event.extra.is_global_practice_event,
    staffMember: formatStaffMemberFullName(
      event.staff_member as MinimalStaffMember
    ),
    staffMemberId: Number((event.staff_member as MinimalStaffMember)?.id),
    // First type drives colour and the type a reopened block seeds; ENG-2105 needs all of them
    // to decide whether a booking fits a multi-type slot.
    appointmentType: event.extra.appointment_types[0],
    appointmentTypes: event.extra.appointment_types,
    rRule: "",
    rrule,
    created_at: event.created_at,
    created_by: event.created_by,
    updated_at: event.updated_at,
    updated_by: event.updated_by,
    start_moment: event.start_moment,
    end_moment: event.end_moment
  };
  return appointmentBlock;
}

export function isAppointmentEvent(
  event: EventResponse
): event is AppointmentEvent {
  return event.type === "A";
}

export function isAppointmentBlockAsScheduleEvent(
  event: EventResponse
): event is ScheduleEvent {
  return event.type === "S" && Boolean(event.extra?.appointment_types?.length);
}

export function isScheduleEvent(event: EventResponse): event is ScheduleEvent {
  return event.type === "S" && !event.extra.is_available;
}

export function isWorkShiftEvent(
  event: EventResponse
): event is WorkShiftEvent {
  return event.type === EVENT_TYPE_WORK_SHIFT_CODE && event.extra.is_available;
}

export function isAppointmentBlockEvent(
  event: EventResponse
): event is AppointmentBlockEvent {
  return (
    event.type === EVENT_TYPE_SCHEDULE_CODE &&
    event.extra.is_available &&
    event.extra.appointment_types.length > 0
  );
}

export function isPersonalEvent(event: any): event is PersonalEvent {
  return event?.type === EVENT_TYPE_PERSONAL_EVENT;
}

export function isPersonalWorkShiftEvent(
  event: any
): event is PersonalWorkShiftEvent {
  return event?.type === EVENT_TYPE_PERSONAL_WORKSHIFT;
}

export function isClinicEvent(event: any): event is ClinicEvent {
  return event?.type === EVENT_TYPE_CLINIC_EVENT;
}

export function isAppointmentBlock(event: any): event is AppointmentBlock {
  return event?.type === EVENT_TYPE_APPOINTMENT_BLOCK;
}

export function isAppointment(event: any): event is Appointment {
  return event?.type === EVENT_TYPE_APPOINTMENT;
}

export function isAppointmentDeletable(event: any) {
  const appointmentStatus = event?.status;
  const eventTypeIsAppointment = isAppointment(event);
  const isRecurringAppointment = Boolean(event?.schedule);

  const isAppointmentInDeletableState =
    eventTypeIsAppointment &&
    (appointmentStatus === APPOINTMENT_STATUS_DRAFT ||
      appointmentStatus === APPOINTMENT_STATUS_UNCONFIRMED ||
      appointmentStatus === APPOINTMENT_STATUS_CONFIRMED ||
      appointmentStatus === APPOINTMENT_STATUS_CANCELED);
  return (
    isAppointmentInDeletableState ||
    isRecurringAppointment ||
    !eventTypeIsAppointment
  );
}

export function isAppointmentFinalState(event: any) {
  const appointmentStatus = event?.status;
  const eventTypeIsAppointment = isAppointment(event);

  const isAppointmentInFinalState =
    eventTypeIsAppointment &&
    (appointmentStatus === APPOINTMENT_STATUS_NO_SHOW ||
      appointmentStatus === APPOINTMENT_STATUS_COMPLETED);
  return isAppointmentInFinalState || !eventTypeIsAppointment;
}

export function isPersonalEventOrAppointmentBlockInThePast(event: any) {
  return (
    (isPersonalEvent(event) ||
      isPersonalWorkShiftEvent(event) ||
      isClinicEvent(event) ||
      isAppointmentBlock(event)) &&
    isRecurringEvent(event) &&
    isEventInThePast(event)
  );
}

export function isAppointmentEditable(event: any) {
  if (!event) return false;

  return !isPersonalEventOrAppointmentBlockInThePast(event);
}

export function isRecurringEvent(event: any) {
  return (
    event &&
    (event.recurrenceDay ||
      event.recurrencePeriod ||
      event.schedule ||
      event.rrule)
  );
}

export function isGlobalPracticeEvent(event: any) {
  return (
    (isPersonalEvent(event) ||
      isClinicEvent(event) ||
      isAppointmentBlock(event)) &&
    event.isGlobalPracticeEvent
  );
}

export function isEventInThePast(event: any = {}) {
  const { startDate } = event;
  return isBefore(new Date(startDate), new Date());
}

export const allowDragEvent = (event: AppointmentModel) => {
  return !isRecurringEvent(event) && !isAppointmentBlock(event);
};

export const allowResizeEvent = (event: AppointmentModel) => {
  return (
    !isPersonalEvent(event) &&
    !isPersonalWorkShiftEvent(event) &&
    !isRecurringEvent(event) &&
    !isAppointmentBlock(event)
  );
};

export const formatEventsSearchResponse = (response: EventsResponse) => {
  const result: EventSearchOption[] = [];

  response.results.forEach((event) => {
    const clinicTimeZone = event.clinic?.timezone;
    if (isScheduleEvent(event)) {
      result.push(convertEventToPersonalEventModel(event, clinicTimeZone));
    } else if (isAppointmentBlockAsScheduleEvent(event)) {
      const block = {
        ...convertEventToPersonalEventModel(event, clinicTimeZone),
        type: "Block"
      } as AppointmentBlock;
      result.push(block);
    }
  });
  return result;
};

export const formatEventsResponse = (response: EventsResponse) => {
  const result: Events = {
    appointments: [],
    personalEvents: [],
    appointmentBlocks: [],
    personalWorkShift: []
  };

  const workShiftEvents: WorkShiftEvent[] = [];

  response.results.forEach((event) => {
    const clinicTimeZone = event.clinic?.timezone;
    if (isAppointmentEvent(event)) {
      result.appointments.push(
        convertEventToAppointmentModel(event, clinicTimeZone)
      );
    } else if (isScheduleEvent(event)) {
      result.personalEvents.push(
        convertEventToPersonalEventModel(event, clinicTimeZone)
      );
    } else if (isAppointmentBlockEvent(event)) {
      result.appointmentBlocks.push(
        convertEventToAppointmentBlockModel(event, clinicTimeZone)
      );
    } else if (isWorkShiftEvent(event)) {
      result.personalWorkShift.push(
        convertEventToWorkShiftEventModel(event, clinicTimeZone)
      );
      workShiftEvents.push({
        ...event,
        start_moment: (
          convertDateInTimezoneToCurrent(
            event.start_moment,
            clinicTimeZone
          ) as Date
        ).toISOString(),
        end_moment: (
          convertDateInTimezoneToCurrent(
            event.end_moment,
            clinicTimeZone
          ) as Date
        ).toISOString()
      });
    }
  });

  result.workShift = buildStaffMemberWorkShift(workShiftEvents);

  return result;
};

export const getSchedulerContainerWidth = () => {
  return (
    window.document.querySelector(".MainLayout-relativeContainer table")
      ?.clientWidth || 0
  );
};

export const getSchedulerContainerViewPortWidth = () => {
  const container = document.querySelector(
    ".MainLayout-relativeContainer table"
  );
  return container ? container.getBoundingClientRect().width : 0;
};

export const getAppointmentCellWidth = (
  containerWidth: number,
  staffCellIndexes: Record<string, number>,
  viewName: string
) => {
  const cellsCountPerDay = (max(Object.values(staffCellIndexes)) || 0) + 1;
  const daysCount = viewName === "Work Week" ? 5 : viewName === "Week" ? 7 : 1;
  const cellWidth = containerWidth / (daysCount * cellsCountPerDay);

  return cellWidth - 19;
};

export const getAppointmentDates = (events: any[]) => {
  return sortBy(
    events
      ?.filter(
        (event: any) =>
          (event.type === EVENT_TYPE_APPOINTMENT ||
            event.type === EVENT_TYPE_PERSONAL_EVENT ||
            event.type === EVENT_TYPE_CLINIC_EVENT ||
            event.type === EVENT_TYPE_APPOINTMENT_BLOCK) &&
          event.startDate &&
          event.endDate
      )
      .map((event: any) => {
        return {
          id: Number(event.modelId || event.id),
          staffMemberId: event.staffMemberId,
          clinicId: event.clinicId,
          type: event.type,
          start_moment: new Date(event.startDate),
          end_moment: new Date(event.endDate)
        };
      }),
    (event) => event.start_moment
  );
};

const MIN_EVENT_DURATION = 45;
const MINIMAL_OVERLAP_THRESHOLD_MINUTES = 5;

export const getIntersectingEvents = ({
  events,
  id,
  clinicId,
  staffMemberId,
  start_date,
  end_date,
  isGroupByClinicsEnabled
}: {
  events: {
    start_moment: Date;
    end_moment: Date;
    type: string;
    id: number;
    staffMemberId: number;
    clinicId: number;
  }[];
  id: number;
  start_date: Date;
  end_date: Date;
  staffMemberId: number;
  clinicId: number;
  isGroupByClinicsEnabled: boolean;
}): { start_moment: Date; end_moment: Date; type: string }[] => {
  // Calculate the requested interval duration
  const intervalDuration = differenceInMinutes(end_date, start_date);

  // Find all overlapping events regardless of overlap length
  const rawResult = events.filter((event) => {
    const byClinic = isGroupByClinicsEnabled
      ? event.clinicId === clinicId
      : true;
    return (
      event.id !== id &&
      byClinic &&
      event.staffMemberId === staffMemberId &&
      event.start_moment.getTime() < end_date.getTime() &&
      event.end_moment.getTime() > start_date.getTime()
    );
  });

  // If the interval is shorter than 45 minutes, return all raw intersections
  if (intervalDuration < MIN_EVENT_DURATION) {
    return rawResult;
  }

  // Otherwise, filter out events whose overlap is 15 minutes or less
  const filtered = rawResult.filter((event) => {
    const overlapStart = dateMax([event.start_moment, start_date]);
    const overlapEnd = dateMin([event.end_moment, end_date]);
    const overlapMinutes = differenceInMinutes(overlapEnd, overlapStart);
    return overlapMinutes > MINIMAL_OVERLAP_THRESHOLD_MINUTES;
  });

  return filtered;
};
